import { useCallback, useEffect, useRef, useState } from 'react';

import { type Asset } from '@air/api/types';

import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetShortIdForAsset } from '~/swr-hooks/shortUrl/useGetShortIdForAsset';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';

type UseEnsureAssetScopedShareLinksParams = {
  assetId: Asset['id'];
  assetShareLinksItems: AssetScopedShareLinkListItem[];
  analyticsLocation: PrivateAssetOpenLocation | null;
  canCreate: boolean;
  hasResolvedAssetShareLinksList: boolean;
  onCreated?: () => void;
};

export const useEnsureAssetScopedShareLinks = ({
  assetId,
  assetShareLinksItems,
  analyticsLocation,
  canCreate,
  hasResolvedAssetShareLinksList,
  onCreated,
}: UseEnsureAssetScopedShareLinksParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);
  const [hasCreateError, setHasCreateError] = useState(false);
  const hasAttemptedCreate = useRef(false);
  const prevAssetIdRef = useRef(assetId);

  const hasAnyAssetShareLinks = assetShareLinksItems.length > 0;

  const { mutate: createShortIdForAsset, isPending: isCreatingShareLink } = useGetShortIdForAsset();

  const shouldAutoCreate = hasResolvedAssetShareLinksList && !hasAnyAssetShareLinks && canCreate;

  const createShareLink = useCallback(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId || !assetId) {
      return;
    }

    createShortIdForAsset(
      { workspaceId, id: assetId, versionSlot: 'current', ...(analyticsLocation ?? {}) },
      {
        onSuccess: () => {
          setHasCreateError(false);
          setHasCreatedShareLinkInThisSession(true);
          onCreated?.();
        },
        onError: () => {
          setHasCreateError(true);
        },
      },
    );
  }, [analyticsLocation, assetId, createShortIdForAsset, currentWorkspace?.id, onCreated]);

  useEffect(() => {
    if (prevAssetIdRef.current !== assetId) {
      prevAssetIdRef.current = assetId;
      hasAttemptedCreate.current = false;
      setHasCreateError(false);
      setHasCreatedShareLinkInThisSession(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (!shouldAutoCreate || hasAttemptedCreate.current || isCreatingShareLink || hasCreateError) {
      return;
    }

    hasAttemptedCreate.current = true;
    createShareLink();
  }, [createShareLink, hasCreateError, isCreatingShareLink, shouldAutoCreate]);

  const retryCreateShareLink = useCallback(() => {
    if (isCreatingShareLink) {
      return;
    }

    setHasCreateError(false);
    createShareLink();
  }, [createShareLink, isCreatingShareLink]);

  return {
    hasCreateError,
    hasCreatedShareLinkInThisSession,
    isCreatingShareLink,
    retryCreateShareLink,
  };
};
